import { useState, useEffect, useRef } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Save } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"
import type { ArealTranslation } from "@fohet/caravan-booker-shared/types"
import { isBlankHtml } from "@fohet/caravan-booker-shared/utils/html"
import { sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"
import { LocaleTabs, type AppLocale } from "@/components/admin/LocaleTabs"
import { EmailTemplateEditor } from "@/components/editor/EmailTemplateEditor"

type TranslationField = "description" | "operatingRules"

interface TranslationEditorProps {
    arealId: string
    locale: AppLocale
    field: TranslationField
    placeholder?: string
}

const TranslationEditor = ({ arealId, locale, field, placeholder }: TranslationEditorProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { data: translation, isFetched } = useQuery({
        queryKey: ["admin", "translation", arealId, locale],
        queryFn: () => api.get<ArealTranslation>(`/areals/${arealId}/translations/${locale}`),
        enabled: !!arealId,
    })

    const fieldValue = field === "description" ? translation?.description : translation?.operatingRules

    const [value, setValue] = useState("")
    const [isDirty, setIsDirty] = useState(false)
    const initializedRef = useRef(false)

    useEffect(() => {
        if (initializedRef.current) return
        if (!isFetched) return
        initializedRef.current = true
        setValue(fieldValue ?? "")
        setIsDirty(false)
    }, [fieldValue, isFetched])

    const saveMutation = useMutation({
        mutationFn: (html: string) =>
            api.put<ArealTranslation>(`/areals/${arealId}/translations/${locale}`, {
                [field]: isBlankHtml(html) ? null : html,
            }),
        onSuccess: () => {
            toast.success(t("admin.settings.translationToastSaved"))
            setIsDirty(false)
            queryClient.invalidateQueries({ queryKey: ["admin", "translation", arealId, locale] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.translationToastFailed"))
        },
    })

    if (!isFetched) {
        return <div className="text-sm text-slate-400 font-bold">{t("common.loading")}</div>
    }

    return (
        <div className="space-y-3">
            <EmailTemplateEditor
                value={value}
                onChange={(html) => { setValue(html); setIsDirty(true) }}
                availableVariables={[]}
                placeholder={placeholder}
                simpleMode
            />
            <div className="flex justify-end">
                <button
                    type="button"
                    disabled={saveMutation.isPending || !isDirty}
                    onClick={() => saveMutation.mutate(value)}
                    className={primaryBtnClass}
                >
                    <Save className="size-3.5" />
                    {saveMutation.isPending ? t("admin.settings.translationSaving") : t("admin.settings.translationSave")}
                </button>
            </div>
        </div>
    )
}

interface LocalizedTextSectionProps {
    arealId: string
    field: TranslationField
    title: string
    placeholder?: string
}

export const LocalizedTextSection = ({ arealId, field, title, placeholder }: LocalizedTextSectionProps) => {
    const [activeLocale, setActiveLocale] = useState<AppLocale>("cs")

    return (
        <div className={sectionClass}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className={sectionHeaderClass}>{title}</h2>
                <LocaleTabs value={activeLocale} onChange={setActiveLocale} />
            </div>
            <TranslationEditor
                key={`${field}-${activeLocale}`}
                arealId={arealId}
                locale={activeLocale}
                field={field}
                placeholder={placeholder}
            />
        </div>
    )
}
